import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Alert,
  App,
  Avatar,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Rate,
  Select,
  Space,
  Typography,
  Upload,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useRobots } from "@/entities/robot";
import { errorMessage } from "@/shared/api";

import { useCreatePersonaComment } from "../model/useCreatePersonaComment";

/**
 * Отзыв от лица вымышленного участника (доработки §6.2, §7 «Comments»).
 *
 * **Модалка на странице очереди, а не отдельный раздел.** Форма из пяти полей;
 * второй пункт меню размыл бы «Отзывы», которые сейчас однозначно означают
 * «очередь, требующая решения» — на этом построен бейдж в навигации.
 *
 * **Кнопка видна только суперадмину.** Клиентская проверка не защита — сервер
 * отвечает 403 независимо. Она нужна ради другого: оператор не должен видеть
 * кнопку, которая всегда отказывает; тот же довод записан в докстринге
 * `SuperadminOnly` в `app/routes.tsx`.
 *
 * **Дата обязательна.** Без неё десяток отзывов, заведённых подряд, получил бы
 * одну секунду на всех, и вкладка «Новые» в мини-аппе выродилась бы в порядок
 * вставки. Будущее запрещено и здесь, и на сервере: лента сортируется
 * `created_at DESC`, и строка из будущего навсегда осталась бы наверху.
 *
 * Аватарка грузится отдельной ручкой до создания отзыва — комментария в момент
 * выбора файла ещё нет. Она необязательна: без неё мини-апп рисует заглушку с
 * первой буквой имени, поэтому отказ хранилища (501) форму не блокирует.
 */
interface FormValues {
  robot_id: number;
  persona_name: string;
  rating?: number;
  text: string;
  created_at: Dayjs;
}

export function CreatePersonaCommentButton({ onCreated }: { onCreated?: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [open, setOpen] = useState(false);
  const [avatarKey, setAvatarKey] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const robots = useRobots();
  const { create, uploadAvatar } = useCreatePersonaComment();

  const personaName = Form.useWatch("persona_name", form);

  const close = () => {
    setOpen(false);
    form.resetFields();
    setAvatarKey(null);
    setAvatarUrl(null);
  };

  const choose = (file: File) => {
    uploadAvatar.mutate(file, {
      onSuccess: (data) => {
        setAvatarKey(data.key);
        setAvatarUrl(data.url ?? null);
        void message.success(t("comments.persona.avatarUploaded"));
      },
    });
  };

  const submit = (values: FormValues) => {
    create.mutate(
      {
        robot_id: values.robot_id,
        persona_name: values.persona_name,
        rating: values.rating ?? null,
        text: values.text,
        created_at: values.created_at.toISOString(),
        persona_avatar_key: avatarKey,
      },
      {
        onSuccess: () => {
          void message.success(t("comments.persona.created"));
          close();
          onCreated?.();
        },
      },
    );
  };

  return (
    <>
      <Button type="primary" icon={<PlusOutlined aria-hidden />} onClick={() => { setOpen(true); }}>
        {t("comments.persona.add")}
      </Button>

      <Modal
        open={open}
        title={t("comments.persona.title")}
        onCancel={close}
        onOk={() => { form.submit(); }}
        okText={t("comments.persona.submit")}
        cancelText={t("common.cancel")}
        confirmLoading={create.isPending}
        destroyOnHidden
        width={640}
      >
        <Alert
          type="warning"
          showIcon
          message={t("comments.persona.warning")}
          style={{ marginBottom: 16 }}
        />

        {robots.isError && (
          <Alert
            type="error"
            showIcon
            message={errorMessage(robots.error)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{ created_at: dayjs() }}
        >
          <Form.Item
            name="robot_id"
            label={t("comments.persona.robot")}
            rules={[{ required: true, message: t("comments.persona.robotRequired") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={robots.isPending}
              options={(robots.data ?? []).map((robot) => ({
                value: robot.id,
                label: robot.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="persona_name"
            label={t("comments.persona.name")}
            rules={[
              { required: true, message: t("comments.persona.nameRequired") },
              { min: 2, max: 64, message: t("comments.persona.nameLength") },
            ]}
          >
            <Input maxLength={64} placeholder={t("comments.persona.namePlaceholder")} />
          </Form.Item>

          <Form.Item label={t("comments.persona.avatar")}>
            <Space size={12} align="center">
              <Avatar size={44} src={avatarUrl}>
                {(personaName ?? "").charAt(0).toUpperCase()}
              </Avatar>
              <Upload
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
                // antd иначе пойдёт слать файл сам, своим запросом мимо клиента
                // и мимо токена админки.
                beforeUpload={(file) => {
                  choose(file);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined aria-hidden />} loading={uploadAvatar.isPending}>
                  {t("comments.persona.avatarChoose")}
                </Button>
              </Upload>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("comments.persona.avatarHint")}
              </Typography.Text>
            </Space>
          </Form.Item>

          <Form.Item name="rating" label={t("comments.persona.rating")}>
            <Rate allowClear />
          </Form.Item>

          <Form.Item
            name="text"
            label={t("comments.persona.text")}
            rules={[
              { required: true, message: t("comments.persona.textRequired") },
              { min: 2, max: 2000, message: t("comments.persona.textLength") },
            ]}
          >
            <Input.TextArea rows={5} maxLength={2000} showCount />
          </Form.Item>

          <Form.Item
            name="created_at"
            label={t("comments.persona.createdAt")}
            extra={t("comments.persona.createdAtHint")}
            rules={[
              { required: true, message: t("comments.persona.createdAtRequired") },
              {
                validator: (_rule, value: Dayjs | null) =>
                  value && value.isAfter(dayjs())
                    ? Promise.reject(new Error(t("comments.persona.createdAtFuture")))
                    : Promise.resolve(),
              },
            ]}
          >
            <DatePicker
              showTime
              format="DD.MM.YYYY HH:mm"
              style={{ width: "100%" }}
              // Отсекает будущее крупным шагом; точную границу по минутам
              // сторожит валидатор выше, а окончательно — сервер.
              disabledDate={(current) => current.isAfter(dayjs(), "day")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
